/**
 * ASİSTAN SOHBETİ — paylaşımlı çekirdek: /assistant sayfası ve her sayfayı takip
 * eden yüzen widget aynı bileşeni kullanır. Sesli döngü (Scribe STT + ElevenLabs
 * TTS), tablo kartları, uygulama içi yönlendirme, kalıcı geçmiş. Asistan,
 * kullanıcının o an baktığı sayfayı bilir (context) ve ona göre yardım eder.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card, CardContent } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Send, Mic, Ear, Volume2, VolumeX, Trash2, ArrowRight, Plus, MessageSquare, X, Square, Paperclip } from "lucide-react";
import { AssistantOrb, type OrbState } from "@/components/assistant-orb";
import { assistantStore, useAssistantStore } from "@/lib/assistant-store";
import { playVoice, stopVoice, isSpeaking, enqueueSpeech, waitSpeechEnd } from "@/lib/voice-bus";
import { confirmDialog } from "@/components/confirm-dialog";

type Meta = { actions?: { path: string; label: string; highlight?: string; switchDomainId?: number }[]; table?: { title: string; columns: string[]; rows: string[][] } | null; endConversation?: boolean; approval?: { tool: string; input: unknown; label: string; tokens: number } };
type Msg = { id?: number; conversationId?: number; role: "user" | "assistant"; content: string; meta?: Meta | null };
type Convo = { id: number; title: string; lastAt: string; count: number };

// Düşünürken sessizlik olmasın: dönen durum metinleri + sesli modda dolgu cümleleri.
// MANTIK (kullanıcı isteği): her şeye "bakıyorum" DEMEZ. Mesajın türüne göre davranır:
// - İŞ İSTEĞİ ("aç", "tara", "üret"...) → HEMEN kabul eder: "Tamam, bakıyorum ona, biraz bekleteceğim."
// - SORU → ancak düşünme UZARSA (5sn+) kısa bir "verilere bakıyorum" der
// - Sohbet → hiç araya girmez
// Uzun süren her işte 13. saniyede "az kaldı" tarzı ikinci bir laf. Varyantlar rastgele,
// sesler tek sefer üretilip önbelleklenir.
const MODEL_AD = { hizli: "Hızlı", denge: "Dengeli", derin: "Derin" } as const;
const MODEL_NOT = {
  hizli: "Kısa sorular, en ucuz ve en çabuk",
  denge: "Günlük iş için varsayılan",
  derin: "Strateji ve karmaşık muhakeme, yavaş ve pahalı",
} as const;
// Canlı geri bildirim: "hep 'düşünüyorum bakıyorum analiz ediyorum' geliyor" —
// aynı ailenin üç varyantı dönünce robotik duyuluyordu. Kısa, nötr ve çeşitli.
const BUSY_TEXTS = ["hazırlanıyor…", "veriler toplanıyor…", "az kaldı…"];
const FILLERS_ACK = ["Tamam.", "Oldu, hemen.", "Peki, birkaç saniye.", "Hallediyorum."];
const FILLERS_THINK = ["Bir saniye.", "Hemen kontrol ediyorum.", "Şu rakamları toparlayayım."];
const FILLERS_LATE = ["Az kaldı.", "Biraz uzun sürdü, bir saniye daha.", "Topluyorum, geliyorum."];
// İş isteği mi? (aç/göster/tara/üret/başlat... geçiyorsa anında kabul cümlesi)
const ACTION_RX = /(aç|göster|tara|tarat|başlat|üret|oluştur|hazırla|getir|güçlendir|ekle|kaydet|çalıştır|incele|analiz et)/i;
// Soru mu? (soru işareti ya da soru kelimesi)
const QUESTION_RX = /\?|(^|\s)(ne|neden|niye|nasıl|kaç|hangi|kim|nerede|mi|mı|mu|mü)(\s|$)/i;

// withThreads: tam sayfa görünümünde sağda ChatGPT tarzı sohbet geçmişi listesi açılır
// voiceControlled: sidebar orb'u store üzerinden sesli modu açıp kapatır (panel gerekmez)
export function AssistantChat({ compact = false, withThreads = false, voiceControlled = false, onOrbState, onClose }: { compact?: boolean; withThreads?: boolean; voiceControlled?: boolean; onOrbState?: (s: OrbState) => void; onClose?: () => void }) {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [orb, setOrbRaw] = useState<OrbState>("idle");
  const setOrb = (s: OrbState) => { setOrbRaw(s); onOrbState?.(s); assistantStore.setOrb(s); };
  // Sohbet dizisi: null = en son sohbet yüklenir, 0 = yeni (boş) sohbet, sayı = o sohbet
  const [convId, setConvId] = useState<number | null>(null);
  const [voiceOn, setVoiceOn] = useState(true);
  // Akış kapanışları (scheduleTts) render anındaki voiceOn'u yakalıyordu — sessize
  // alınca ESKİ true değerini görüp cümle eklemeye devam ediyordu. Ref her zaman güncel.
  const voiceOnRef = useRef(true);
  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);
  const [voiceErr, setVoiceErr] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  const { data: history } = useQuery({
    queryKey: ["assistant-history", activeDomainId, convId],
    enabled: !!activeDomainId && convId !== 0, // 0 = yeni boş sohbet, sunucudan çekilmez
    staleTime: 60_000,
    queryFn: async (): Promise<Msg[]> => {
      try {
        const r = await fetch(`/api/domains/${activeDomainId}/assistant/history${convId ? `?conversationId=${convId}` : ""}`);
        return r.ok ? await r.json() : [];
      } catch { return []; }
    },
  });
  useEffect(() => {
    if (!history) return;
    // Sohbet değişince tam yükle; aynı sohbette ise kısalmaya izin verme (yazım sırasında üzerine yazma koruması)
    if (convId === null || history.length >= messages.length) setMessages(history);
    if (convId === null && history.length) setConvId(history[history.length - 1].conversationId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  // Sohbet listesi (geçmiş dizileri) — tam sayfada panel, kompaktta sadece "yeni sohbet" için
  const { data: convos } = useQuery({
    queryKey: ["assistant-conversations", activeDomainId],
    enabled: !!activeDomainId,
    staleTime: 30_000,
    queryFn: async (): Promise<Convo[]> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/assistant/conversations`); return r.ok ? await r.json() : []; } catch { return []; }
    },
  });
  const openConvo = (id: number) => { if (id === convId) return; setMessages([]); setConvId(id); };
  // TEK SOHBET SİLME (kullanıcı isteği: "GPT gibi soldaki bardan direkt silebilelim").
  // Sunucu ucu zaten vardı (DELETE ?conversationId=); eksik olan liste satırındaki buton.
  const sohbetiSil = async (id: number, baslik: string) => {
    if (!activeDomainId) return;
    const ok = await confirmDialog({
      title: "Sohbeti sil",
      description: `"${baslik}" sohbeti ve tüm mesajları kalıcı olarak silinsin mi?`,
      confirmText: "Sil", danger: true,
    });
    if (!ok) return;
    await fetch(`/api/domains/${activeDomainId}/assistant/history?conversationId=${id}`, { method: "DELETE" });
    if (id === convId) { setMessages([]); setConvId(0); } // aktif sohbet silindiyse boş sohbete dön
    queryClient.invalidateQueries({ queryKey: ["assistant-conversations", activeDomainId] });
    queryClient.invalidateQueries({ queryKey: ["assistant-history", activeDomainId] });
  };
  const newConvo = () => { setMessages([]); setConvId(0); };

  const resumeIfConvo = () => { if (convoRef.current) void startListening(); };
  // speak(): TTS bitene kadar BEKLEYEN promise döndürür (tur senkronu buna dayanır).
  // TÜM ses çalma TEK KANALDAN (voice-bus) geçer: yeni söz başlarken önceki ses durur,
  // dolgu sesi sözün üstüne binmez, ikinci sohbet bileşeni monte olsa da tek hoparlör kalır.
  const speak = async (text: string): Promise<void> => {
    if (!voiceOn) { resumeIfConvo(); return; }
    // Uzun cevaplarda kesme CÜMLE SONUNDA olsun (cümle ortasında susup "niye kestin" dedirtmesin).
    let spoken = text.slice(0, 2400);
    if (text.length > 2400) {
      const cut = Math.max(spoken.lastIndexOf("."), spoken.lastIndexOf("!"), spoken.lastIndexOf("?"));
      if (cut > 200) spoken = spoken.slice(0, cut + 1);
    }
    try {
      // 20 sn TTS zaman aşımı: uç nokta asılı kalırsa (ElevenLabs/ağ) sonsuza dek beklenmez
      const r = await fetch("/api/assistant/tts", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: spoken }), signal: AbortSignal.timeout(20_000) });
      if (!r.ok) { setVoiceErr(((await r.json().catch(() => ({}))) as { error?: string }).error ?? "Ses üretilemedi"); resumeIfConvo(); return; }
      setVoiceErr(null);
      const blob = await r.blob();

      // SÖZ KESME (barge-in): konuşurken mikrofonda 0.35 sn sürekli insan sesi → sözü kes, dinle.
      let bargeTimer: number | null = null;
      const clearBarge = () => { if (bargeTimer) { window.clearInterval(bargeTimer); bargeTimer = null; } };
      if (convoRef.current && streamRef.current && audioCtxRef.current) {
        try {
          const bctx = audioCtxRef.current;
          if (bctx.state === "suspended") void bctx.resume();
          const bsrc = bctx.createMediaStreamSource(streamRef.current);
          const ban = bctx.createAnalyser(); ban.fftSize = 1024; bsrc.connect(ban);
          const bbuf = new Uint8Array(ban.fftSize);
          let loudSince = 0;
          bargeTimer = window.setInterval(() => {
            ban.getByteTimeDomainData(bbuf);
            let ssum = 0;
            for (let i = 0; i < bbuf.length; i++) { const v = (bbuf[i] - 128) / 128; ssum += v * v; }
            const brms = Math.sqrt(ssum / bbuf.length);
            const bnow = Date.now();
            // Eşikler sıkılaştırıldı: 0.05/350ms televizyon, klavye, kapı zili gibi ortam
            // gürültüsünde konuşmayı ortadan kesiyordu ("ses takılıyor" şikayeti) —
            // kesme için daha yüksek ve daha SÜREKLİ insan sesi gerekir.
            if (brms > 0.09) { if (!loudSince) loudSince = bnow; } else loudSince = 0;
            if (loudSince && bnow - loudSince > 600) { clearBarge(); stopVoice(); } // stopVoice → promise çözülür
          }, 90);
        } catch { /* barge-in kurulamazsa normal akış sürer */ }
      }

      // Bekçi metin uzunluğuna göre: varsayılan 120 sn, takılan sesi 2 dk sessizce
      // bekletiyordu (canlı tur vakası) — konuşma süresi + pay kadar beklenir.
      await playVoice(blob, "speech", { onStart: () => setOrb("speaking"), watchdogMs: Math.min(60_000, Math.max(15_000, spoken.split(/\s+/).length * 800 + 8_000)) });
      clearBarge();
      setOrb("idle");
      resumeIfConvo();
    } catch { setVoiceErr("Ses üretilemedi"); resumeIfConvo(); }
  };

  // Sesli modda dolgu: yalnız GERÇEKTEN uzun süren düşünmede araya girer (önbellekli sesler)
  const fillerCacheRef = useRef<Map<string, Blob>>(new Map());
  const busyRef = useRef(false);
  const playFiller = async (pool: string[]) => {
    if (!busyRef.current || !convoRef.current || !voiceOn) return;
    try {
      const text = pool[Math.floor(Math.random() * pool.length)];
      let blob = fillerCacheRef.current.get(text) ?? null;
      if (!blob) {
        const r = await fetch("/api/assistant/tts", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text }) });
        if (!r.ok) return;
        blob = await r.blob();
        fillerCacheRef.current.set(text, blob);
      }
      if (!busyRef.current || isSpeaking()) return; // cevap geldiyse ya da söz çalıyorsa araya girme
      void playVoice(blob, "filler", { watchdogMs: 20_000 });
    } catch { /* dolgu kritik değil */ }
  };
  // Dolgu zamanlayıcıları İPTAL EDİLEBİLİR: streaming'de ilk delta gelince ve cevap bitince
  // temizlenir — veda anında "az kaldı, topluyorum" çalması bu yüzden oluyordu.
  const fillerTimersRef = useRef<number[]>([]);
  const clearFillers = () => { fillerTimersRef.current.forEach((t) => window.clearTimeout(t)); fillerTimersRef.current = []; };
  const playFillerIfStillBusy = (userMsg: string) => {
    clearFillers();
    // Veda/kapatma mesajında HİÇ dolgu yok — kısa veda zaten saniyeler içinde akar
    if (/görüşürüz|kapat|hoşça ?kal|bay bay|iyi geceler/i.test(userMsg)) return;
    if (ACTION_RX.test(userMsg)) {
      fillerTimersRef.current.push(window.setTimeout(() => void playFiller(FILLERS_ACK), 900));
    } else if (QUESTION_RX.test(userMsg)) {
      fillerTimersRef.current.push(window.setTimeout(() => void playFiller(FILLERS_THINK), 5000));
    }
    fillerTimersRef.current.push(window.setTimeout(() => void playFiller(FILLERS_LATE), 13000));
  };

  // STREAMING: cevap delta delta akar; sesli modda cümleler ÜRETİLDİKÇE seslendirilir
  // (ilk ses hedefi <1sn — "tam cevabı bekle" sessizliği biter). TTS istekleri sıralı
  // zincirde gider, ses kuyruğu (voice-bus) cümleleri arka arkaya çalar.
  // ONAY KARTI durumu (v2-E): işlenen/vazgeçilen kartlar mesaj indeksiyle saklanır
  const [approvalDone, setApprovalDone] = useState<Set<number>>(new Set());
  const [approvalBusy, setApprovalBusy] = useState<number | null>(null);
  const approveTool = async (idx: number, approval: { tool: string; input: unknown; label: string; tokens: number }) => {
    if (!activeDomainId || approvalBusy !== null) return;
    setApprovalBusy(idx);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/assistant/approve-tool`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ tool: approval.tool, input: approval.input, conversationId: convId || 0 }),
      });
      const j = (await r.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!r.ok) throw new Error(j.error ?? "Başlatılamadı");
      setApprovalDone((s) => new Set(s).add(idx));
      setMessages((m) => [...m, { role: "assistant", content: j.message ?? `✅ ${approval.label} başlatıldı.` }]);
      queryClient.invalidateQueries();
      void speak(j.message ?? `${approval.label} başlatıldı.`);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${(e as Error).message}` }]);
    } finally { setApprovalBusy(null); }
  };

  const streamAbortRef = useRef<AbortController | null>(null);
  // DURDUR: akışı keser. Sunucu tarafında da iptal edilir (bağlantı kapanınca
  // req "close" olayı tetiklenir), böylece boşuna token yanmaz.
  const durdur = () => { streamAbortRef.current?.abort(); stopVoice(); };
  // ÇİFT GÖNDERİM KORUMASI: sesli döngü aynı sözü iki kez STT'leyip iki kez gönderebiliyordu
  // (gece kayıtlarında 6 çift mesaj + birbirine zıt çift cevaplar) — 12 sn içinde aynı/benzer
  // metin ikinci kez gelirse yutulur.
  const lastSentRef = useRef<{ text: string; at: number }>({ text: "", at: 0 });

  // MODEL SEÇİMİ: hız / maliyet dengesi kullanıcıda. Seçim tarayıcıda kalıcı.
  const [model, setModel] = useState<"hizli" | "denge" | "derin">(
    () => (localStorage.getItem("geolop:model") as "hizli" | "denge" | "derin") || "denge",
  );
  const modelSec = (m: "hizli" | "denge" | "derin") => { setModel(m); localStorage.setItem("geolop:model", m); };

  // DOSYA EKLEME — iki yol:
  //   METİN türleri (txt/csv/md/json/xml/html…): tarayıcıda okunur, mesaja metin olarak eklenir.
  //   İKİLİ türler (görsel/PDF/Excel): base64 olarak SUNUCUYA gider; sunucu gerçek dosya
  //   imzasını (magic bytes) doğrular, Claude'a iletir ve ATAR — hiçbir dosya saklanmaz.
  const DOSYA_TURLERI = ".txt,.csv,.tsv,.md,.json,.log,.xml,.htm,.html,.png,.jpg,.jpeg,.webp,.gif,.pdf,.xlsx,.xls";
  const DOSYA_SINIR = 400_000;        // metin: ~100 bin token
  const GORSEL_SINIR = 6 * 1024 * 1024;  // görsel 6 MB
  const PDF_SINIR = 8 * 1024 * 1024;     // PDF 8 MB
  const EXCEL_SINIR = 4 * 1024 * 1024;   // Excel 4 MB
  const [ek, setEk] = useState<{ ad: string; metin: string } | null>(null);
  const [ekIkili, setEkIkili] = useState<{ ad: string; mime: string; b64: string; tur: "gorsel" | "pdf" | "excel"; boyut: number } | null>(null);
  const dosyaRef = useRef<HTMLInputElement>(null);
  const b64Oku = (f: File) => new Promise<string>((cozul, hata) => {
    const r = new FileReader();
    r.onload = () => cozul(String(r.result).split(",")[1] ?? "");
    r.onerror = () => hata(new Error("okunamadı"));
    r.readAsDataURL(f);
  });
  const dosyaSec = async (f: File | null | undefined) => {
    if (!f) return;
    const uzanti = (f.name.split(".").pop() ?? "").toLowerCase();
    try {
      if (["png", "jpg", "jpeg", "webp", "gif"].includes(uzanti)) {
        if (f.size > GORSEL_SINIR) { setVoiceErr(`Görsel çok büyük (${Math.round(f.size / 1024 / 1024)} MB). Sınır 6 MB.`); return; }
        const mime = uzanti === "jpg" ? "image/jpeg" : `image/${uzanti === "jpeg" ? "jpeg" : uzanti}`;
        setEkIkili({ ad: f.name, mime, b64: await b64Oku(f), tur: "gorsel", boyut: f.size });
        setEk(null); setVoiceErr(null); return;
      }
      if (uzanti === "pdf") {
        if (f.size > PDF_SINIR) { setVoiceErr(`PDF çok büyük (${Math.round(f.size / 1024 / 1024)} MB). Sınır 8 MB.`); return; }
        setEkIkili({ ad: f.name, mime: "application/pdf", b64: await b64Oku(f), tur: "pdf", boyut: f.size });
        setEk(null); setVoiceErr(null); return;
      }
      if (["xlsx", "xls"].includes(uzanti)) {
        if (f.size > EXCEL_SINIR) { setVoiceErr(`Excel çok büyük (${Math.round(f.size / 1024 / 1024)} MB). Sınır 4 MB.`); return; }
        setEkIkili({ ad: f.name, mime: uzanti === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/vnd.ms-excel", b64: await b64Oku(f), tur: "excel", boyut: f.size });
        setEk(null); setVoiceErr(null); return;
      }
      // Metin türleri — eski yol
      if (f.size > DOSYA_SINIR) { setEk(null); setVoiceErr(`Dosya çok büyük (${Math.round(f.size / 1024)} KB). Sınır 400 KB.`); return; }
      const metin = await f.text();
      if (!metin.trim()) { setVoiceErr("Dosya boş görünüyor."); return; }
      setEk({ ad: f.name, metin });
      setEkIkili(null); setVoiceErr(null);
    } catch { setVoiceErr("Dosya okunamadı."); }
  };
  const send = async (text?: string) => {
    let msg = (text ?? input).trim();
    // Ek varsa mesajın SONUNA açıkça işaretlenmiş blok olarak eklenir; asistan
    // neyin kullanıcı sorusu neyin dosya içeriği olduğunu karıştırmasın.
    if (ek) {
      msg = `${msg || `"${ek.ad}" dosyasını inceler misin?`}

[EKLENEN DOSYA: ${ek.ad}]
${ek.metin}`;
    }
    if (ekIkili && !msg) msg = `"${ekIkili.ad}" dosyasını inceler misin?`;
    if (!msg || !activeDomainId || busy) return;
    const norm = msg.toLocaleLowerCase("tr").replace(/[^a-zçğıöşü0-9]/g, "");
    if (norm && norm === lastSentRef.current.text && Date.now() - lastSentRef.current.at < 12_000) return;
    lastSentRef.current = { text: norm, at: Date.now() };
    setInput("");
    const gidenIkili = ekIkili;
    setEk(null); setEkIkili(null); // ek gönderildi, çubuk temizlenir
    if (dosyaRef.current) dosyaRef.current.value = "";
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true); busyRef.current = true; setOrb("thinking");
    playFillerIfStillBusy(msg);

    // Cümle → TTS zinciri (sıra korunur; ses kuyruğa eklenir)
    let ttsChain: Promise<void> = Promise.resolve();
    let sentBuf = "";
    // DOLGU SÜZGECİ (ses): sunucu nihai metni temizliyor ama SES delta akışından
    // beslendiği için "bir bakayım neler olmuş" cümlesi temizlikten ÖNCE kuyruğa
    // girebiliyor. İlk 2 cümlede kısa dolgu kalıbı seslendirilmez.
    let ttsCumleNo = 0;
    const DOLGU_SES = /(bakayım|bakıyorum|bakalım|kontrol edeyim|kontrol ediyorum|analiz ediyorum|inceleyeyim|neler olmuş)/i;
    const scheduleTts = (sentence: string) => {
      const sira = ttsCumleNo++;
      if (sira < 2 && sentence.length <= 60 && !sentence.trim().endsWith("?") && DOLGU_SES.test(sentence)) return;
      const s = sentence.trim();
      if (!voiceOnRef.current || s.length < 2) return;
      ttsChain = ttsChain.then(async () => {
        if (!voiceOnRef.current) return; // cevap ortasında sessize alındıysa kalan cümleler seslendirilmez
        try {
          const r = await fetch("/api/assistant/tts", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: s.slice(0, 600) }) });
          if (r.ok && voiceOnRef.current) enqueueSpeech(await r.blob(), { onStart: () => setOrb("speaking") });
        } catch { /* tek cümle düşerse akış sürer */ }
      });
    };
    const feedSentences = (delta: string, flushAll = false) => {
      sentBuf += delta;
      // Cümle sonlarında böl; kısa parça SONRAKİ cümleyle birleşene kadar bekletilir.
      // ESKİ HALİ BOZUKTU: continue edilince consumed ilerlemiyor ama regex
      // ilerliyordu → dilim penceresi kayıyordu. İlk cümle kısa olunca ("Selam!")
      // cümleler ortadan kesiliyor, aynı bölge tekrar okunuyor, son kısım hiç
      // seslendirilmiyordu (canlı şikayet: "ilk cümleyi söyleyip duruyor" +
      // "ikinci paragrafı söylemedi"). Düzeltme: dilim SONU daima mutlak konum
      // (m.index + uzunluk); consumed yalnız seslendirilince ilerler — kayma imkansız.
      let m: RegExpExecArray | null;
      const rx = /[^.!?\n]*[.!?\n]+/g;
      let consumed = 0;
      while ((m = rx.exec(sentBuf))) {
        const son = m.index + m[0].length;
        const parca = sentBuf.slice(consumed, son);
        if (parca.trim().length < 12 && !flushAll) continue; // kısa — sonrakiyle biriksin
        scheduleTts(parca);
        consumed = son;
      }
      sentBuf = sentBuf.slice(consumed);
      if (flushAll && sentBuf.trim()) { scheduleTts(sentBuf); sentBuf = ""; }
    };

    // GERÇEK BARGE-IN (streaming): kullanıcı söze girince yalnız ses değil, LLM ÜRETİMİ de kesilir
    let disarmBarge: (() => void) | null = null;
    try {
      const abort = new AbortController();
      streamAbortRef.current = abort;
      if (convoRef.current && streamRef.current && audioCtxRef.current) {
        try {
          const bctx = audioCtxRef.current;
          if (bctx.state === "suspended") void bctx.resume();
          const bsrc = bctx.createMediaStreamSource(streamRef.current);
          const ban = bctx.createAnalyser(); ban.fftSize = 1024; bsrc.connect(ban);
          const bbuf = new Uint8Array(ban.fftSize);
          let loudSince = 0;
          const t = window.setInterval(() => {
            ban.getByteTimeDomainData(bbuf);
            let ssum = 0;
            for (let i = 0; i < bbuf.length; i++) { const v = (bbuf[i] - 128) / 128; ssum += v * v; }
            const brms = Math.sqrt(ssum / bbuf.length);
            const bnow = Date.now();
            if (brms > 0.09) { if (!loudSince) loudSince = bnow; } else loudSince = 0;
            if (loudSince && bnow - loudSince > 600) { stopVoice(); streamAbortRef.current?.abort(); disarmBarge?.(); }
          }, 90);
          disarmBarge = () => { window.clearInterval(t); disarmBarge = null; };
        } catch { /* barge kurulamazsa akış sürer */ }
      }
      const r = await fetch(`/api/domains/${activeDomainId}/assistant/chat-stream`, {
        method: "POST", headers: { "content-type": "application/json" },
        // SESLİ görüşmede model zorla "hizli": ölçüldü — ilk cümle Hızlı'da 5.8 sn,
        // Dengeli'de 15.8 sn (araç turları bitmeden konuşmuyor). 15 saniyelik sessizlik
        // sesli sohbeti öldürüyor; yazılı sohbette kullanıcının seçimi geçerli kalır.
        body: JSON.stringify({ message: msg, context: locationRef.current, conversationId: convId || 0, model: convoRef.current ? "hizli" : model, sesli: convoRef.current, ek: gidenIkili ? { ad: gidenIkili.ad, mime: gidenIkili.mime, b64: gidenIkili.b64, tur: gidenIkili.tur } : undefined }),
        signal: abort.signal,
      });
      if (!r.ok || !r.body) {
        const dErr = await r.json().catch(() => ({} as { error?: string }));
        throw new Error((dErr as { error?: string }).error ?? "Asistan hatası");
      }
      // ndjson satırlarını oku: delta'lar mesaj balonuna canlı yazılır
      setMessages((m) => [...m, { role: "assistant", content: "" }]);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let lineBuf = "";
      let d: { reply?: string; meta?: Meta; conversationId?: number; error?: string } = {};
      let streamed = "";
      // NÖBETÇİ: sunucu 5 sn'de bir nabız atıyor. 30 sn hiç bayt gelmezse bağlantı
      // sessizce ölmüş demektir — sonsuza kadar "yazıyor..." beklemek yerine
      // akışı kesip elimizdeki metni koruyoruz. Canlıdaki "durdu kaldı" buydu.
      let sonVeri = Date.now();
      const nobetci = window.setInterval(() => {
        if (Date.now() - sonVeri > 30_000) { window.clearInterval(nobetci); abort.abort(); }
      }, 3000);
      try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        sonVeri = Date.now();
        lineBuf += decoder.decode(value, { stream: true });
        const lines = lineBuf.split("\n");
        lineBuf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.trim()) continue;
          let ev: { type?: string; text?: string; reply?: string; meta?: Meta; conversationId?: number; error?: string };
          try { ev = JSON.parse(line); } catch { continue; }
          if (ev.type === "ping") { continue; } // canlılık işareti — gösterilmez
          if (ev.type === "delta" && ev.text) {
            clearFillers(); // cevap akmaya başladı — dolgu sesleri iptal
            streamed += ev.text;
            const shown = streamed.replace(/\s*[—–]\s*/g, ", "); // sunucudaki tire temizliğinin canlı eşi
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: shown } : x)));
            feedSentences(ev.text.replace(/\s*[—–]\s*/g, ", "));
          } else if (ev.type === "done") {
            d = ev;
          } else if (ev.type === "error") {
            throw new Error(ev.error ?? "Asistan hatası");
          }
        }
      }
      } finally { window.clearInterval(nobetci); }
      feedSentences("", true); // kalan yarım cümleyi de seslendir
      // YARIM METİN KORUNUR: akış koptuysa yazılanı SİLMEK yerine ekranda bırak,
      // sonuna not düş. Önceden hata fırlatılıp balon boşalıyordu ve kullanıcı
      // gözünün önünde yazılan cevabı kaybediyordu.
      if (!d.reply) {
        if (streamed.trim()) {
          const not = `${streamed.trim()}\n\n_(bağlantı yarıda kesildi, cevap eksik olabilir)_`;
          setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: not } : x)));
          queryClient.invalidateQueries({ queryKey: ["assistant-conversations", activeDomainId] });
          return;
        }
        throw new Error("Akış yarım kaldı — bir daha dener misin?");
      }
      // Nihai metin + meta son balona yazılır (araç turlarında birleşen tam cevap)
      setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: d.reply!, meta: d.meta } : x)));
      if (d.conversationId && d.conversationId !== convId) setConvId(d.conversationId);
      queryClient.invalidateQueries({ queryKey: ["assistant-conversations", activeDomainId] });
      // CANLI GÖRÜNÜRLÜK: asistan bir şey oluşturduysa/başlattıysa (brief, tarama...)
      // açık sayfadaki TÜM sorgular tazelenir — kullanıcı yenilemeden ekranda belirir.
      queryClient.invalidateQueries();
      // Geolop Asistanı: sesli modda asistan yönlendirme verdiyse kullanıcıyı OTOMATİK götür + kartı vurgula
      // AKTİF SİTE DEĞİŞİMİ (switch_site): asistan geçişi kullanıcı yerine yapar — sesli/yazılı fark etmez
      const sw = (d.meta?.actions ?? []).find((a) => a.switchDomainId);
      if (sw?.switchDomainId) { setActiveDomainId(sw.switchDomainId); setLocation(sw.path || "/"); }
      const act = d.meta?.actions?.[0];
      const tourSteps = (d.meta as { tour?: { path: string; label: string; text: string }[] } | undefined)?.tour;
      if (tourSteps?.length) assistantStore.startTour(tourSteps, voiceOn ? (t: string) => speak(t) : undefined);
      if (act && convoRef.current) {
        setLocation(act.path);
        assistantStore.setHighlight(act.highlight ?? null);
      }
      // SOHBETİ KAPAT (end_conversation): veda kuyruğu BİTENE kadar bekle, sonra kapat
      if (d.meta?.endConversation) {
        await ttsChain; await waitSpeechEnd();
        if (convoRef.current) endConversation();
        assistantStore.setVoice(false);
        assistantStore.setOpen(false);
        setOrb("idle");
        return;
      }
      // Konuşma kuyruğu bitince orb'u dinlendir ve sesli modda mikrofona geri dön
      void (async () => { await ttsChain; await waitSpeechEnd(); setOrb("idle"); resumeIfConvo(); })();
    } catch (e) {
      const aborted = (e as Error)?.name === "AbortError";
      // DURDURULDU: yazılanı SİLME. Kullanıcı "durdur"a bastığında ya da nöbetçi
      // akışı kestiğinde o ana kadarki metin balonda kalır, sonuna not düşülür.
      if (aborted) {
        setMessages((m) => m.map((x, i) => (
          i === m.length - 1 && x.role === "assistant" && x.content.trim()
            ? { ...x, content: `${x.content.trim()}\n\n_(durduruldu)_` }
            : x
        )).filter((x, i, a) => !(i === a.length - 1 && x.role === "assistant" && !x.content.trim())));
      } else {
        setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${(e as Error).message}` }]);
      }
      setOrb("idle");
      resumeIfConvo();
    } finally { setBusy(false); busyRef.current = false; streamAbortRef.current = null; disarmBarge?.(); clearFillers(); }
  };

  // ═══ ELLER-SERBEST SESLİ DÖNGÜ (Scribe) ═══
  const convoRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const vadTimerRef = useRef<number | null>(null);
  const spokeRef = useRef(false);
  const lastLoudAtRef = useRef(0);
  const startedAtRef = useRef(0);
  const silentTimeoutRef = useRef(false); // dinleme penceresi hiç ses almadan doldu mu

  // ═══ UYANDIRMA MODU (opt-in): bekleme mikrofonu — çift alkış YA DA "uyan/geolop" kelimesi
  // sesli sohbeti başlatır. Bekleme kaydı ayrı MediaRecorder'dadır; sohbet başlarken
  // MUTLAKA durdurulur (aynı akışta iki kayıt mikrofonu susturuyordu — bilinen tuzak).
  const [wakeOn, setWakeOn] = useState(() => localStorage.getItem("geolop:wake") === "1");
  const wakeOnRef = useRef(wakeOn);
  const wakeTimerRef = useRef<number | null>(null);
  const wakeRecRef = useRef<MediaRecorder | null>(null);
  const wakeChunksRef = useRef<Blob[]>([]);
  const wakeLastSttRef = useRef(0);
  const wakeBurstStartRef = useRef(0);
  const wakeLastQuietRef = useRef(0);
  const clapTimesRef = useRef<number[]>([]);
  const segStartRef = useRef(0);
  const [wakeHeard, setWakeHeard] = useState<string | null>(null); // son duyulan kelime (teşhis)

  const stopStandby = () => {
    if (wakeTimerRef.current) { window.clearInterval(wakeTimerRef.current); wakeTimerRef.current = null; }
    try { if (wakeRecRef.current?.state === "recording") wakeRecRef.current.stop(); } catch { /* durmuş */ }
    wakeRecRef.current = null;
  };
  const wakeUp = () => {
    if (convoRef.current) return;
    stopStandby();
    convoRef.current = true;
    assistantStore.setVoice(true);
    void send("[uyandırma] Kullanıcı seni sesle uyandırdı — kısa bir 'buradayım' de ve dinlemeye geç.");
  };
  const startStandby = async () => {
    if (!wakeOnRef.current || convoRef.current || wakeTimerRef.current) return;
    try {
      if (!streamRef.current) streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const stream = streamRef.current;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";

      // SEGMENT KAYDI: parça kırpmak GEÇERSİZ WebM üretiyordu (başlık yalnız ilk parçada) —
      // "uyan" hiç çözülemiyordu. Çözüm: konuşma bitince kaydediciyi DURDUR (tam, geçerli
      // dosya) → STT → yeniden başlat. Sessizlikte 8 sn'de bir sessizce tazelenir.
      let checkOnStop = false;
      const newSegment = () => {
        if (!wakeOnRef.current || convoRef.current) return;
        const rec = new MediaRecorder(stream, { mimeType: mime });
        wakeChunksRef.current = [];
        rec.ondataavailable = (e) => { if (e.data.size > 0) wakeChunksRef.current.push(e.data); };
        rec.onstop = () => {
          const doCheck = checkOnStop; checkOnStop = false;
          const blob = new Blob(wakeChunksRef.current, { type: mime });
          if (doCheck && blob.size > 2000 && wakeOnRef.current && !convoRef.current) {
            void fetch("/api/assistant/stt", { method: "POST", headers: { "content-type": mime }, body: blob })
              .then((r) => r.json()).then((d: { text?: string }) => {
                const heard = (d.text ?? "").trim();
                setWakeHeard(heard || "(boş)");   // kulak butonunun altında görünür — eşik ayarı için
                if (/uyan|uyaan|geolop|gelop|celop|jeolop|hey\s/i.test(heard)) wakeUp();
              }).catch(() => { /* bekleme kontrolü sessiz geçer */ });
          }
          // uyanmadıysak nöbete devam
          if (wakeOnRef.current && !convoRef.current) window.setTimeout(newSegment, 60);
        };
        rec.start();
        wakeRecRef.current = rec;
        wakeBurstStartRef.current = 0;
        segStartRef.current = Date.now();
      };
      newSegment();

      wakeTimerRef.current = window.setInterval(() => {
        if (!wakeOnRef.current || convoRef.current) { stopStandby(); return; }
        analyser.getByteTimeDomainData(buf);
        let sum = 0, peak = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; peak = Math.max(peak, Math.abs(v)); }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();

        // ÇİFT ALKIŞ: iki keskin tepe (120-700 ms arayla) → STT'siz, ücretsiz uyanma
        if (peak > 0.35 && rms > 0.07) {
          const times = clapTimesRef.current;
          if (!times.length || now - times[times.length - 1] > 120) times.push(now);
          if (times.length >= 2 && now - times[times.length - 2] < 700) { wakeUp(); return; }
          if (times.length > 3) times.shift();
        }

        // KONUŞMA PATLAMASI: 0.2-2.5 sn arası ses bitince segmenti kapat → STT kontrolü
        if (rms > 0.025) {
          if (!wakeBurstStartRef.current) wakeBurstStartRef.current = now;
          wakeLastQuietRef.current = now;
        } else if (wakeBurstStartRef.current && now - wakeLastQuietRef.current > 400) {
          const dur = wakeLastQuietRef.current - wakeBurstStartRef.current;
          wakeBurstStartRef.current = 0;
          if (dur >= 200 && dur <= 2500 && now - wakeLastSttRef.current > 3000) {
            wakeLastSttRef.current = now;
            checkOnStop = true;
            try { if (wakeRecRef.current?.state === "recording") wakeRecRef.current.stop(); } catch { /* durmuş */ }
          }
        }
        // Uzayan sessiz segmenti tazele (dosya şişmesin)
        if (!wakeBurstStartRef.current && now - segStartRef.current > 8000) {
          try { if (wakeRecRef.current?.state === "recording") wakeRecRef.current.stop(); } catch { /* durmuş */ }
        }
      }, 90);
    } catch { /* mikrofon izni yoksa bekleme modu sessizce devre dışı */ }
  };
  useEffect(() => {
    wakeOnRef.current = wakeOn;
    localStorage.setItem("geolop:wake", wakeOn ? "1" : "0");
    if (wakeOn) void startStandby(); else stopStandby();
    return () => stopStandby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeOn]);

  const clearVad = () => { if (vadTimerRef.current) { window.clearInterval(vadTimerRef.current); vadTimerRef.current = null; } };
  const stopListening = () => {
    clearVad();
    try { if (recorderRef.current?.state === "recording") recorderRef.current.stop(); } catch { /* durmuş */ }
    setListening(false);
  };
  // keepStore: bileşen unmount olurken (sayfa /assistant sınırından geçerken) store.voice
  // AÇIK bırakılır — diğer chat instance'ı sesli modu kesintisiz devralır.
  const endConversation = (keepStore = false) => {
    convoRef.current = false;
    if (!keepStore) assistantStore.setVoice(false); // sidebar orb'u ve kenar ışıması da kapansın
    stopListening();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => { /* kapalı */ });
    audioCtxRef.current = null;
    stopVoice();   // çalan her sesi kes (tek kanal)
    setOrb("idle");
    if (wakeOnRef.current) window.setTimeout(() => void startStandby(), 400); // uyandırma nöbetine dön
  };
  // DUYAMADIM geri bildirimi: sessizce yeniden dinlemeye dönmek "asistan durdu"
  // hissi veriyordu. Anlaşılmayan her denemede SESLİ uyarı verir, üst üste 3 kez
  // anlaşılmazsa sesli modu kapatır (sonsuz sessiz döngü olmasın).
  const missRef = useRef(0);
  const speakNotice = async (text: string) => {
    try {
      const r = await fetch("/api/assistant/tts", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text }) });
      if (!r.ok) return;
      await playVoice(await r.blob(), "speech", { watchdogMs: 20_000 });
      resumeIfConvo();
    } catch { resumeIfConvo(); }
  };
  const handleMiss = (reason: string) => {
    missRef.current += 1;
    setVoiceErr(reason);
    if (missRef.current >= 3) {
      missRef.current = 0;
      void speakNotice("Seni duyamıyorum. Mikrofonunu kontrol edip tekrar başlatır mısın?");
      endConversation();
      return;
    }
    void speakNotice(missRef.current === 1 ? "Seni tam duyamadım, bir daha söyler misin?" : "Hâlâ duyamıyorum, biraz daha yüksek sesle söyle lütfen.");
  };

  const transcribeAndSend = async (blob: Blob) => {
    if (!convoRef.current) return;
    setOrb("thinking");
    try {
      const r = await fetch("/api/assistant/stt", { method: "POST", headers: { "content-type": blob.type || "audio/webm" }, body: blob });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error ?? "Ses çözülemedi");
      const text = (d.text ?? "").trim();
      if (text) { setVoiceErr(null); missRef.current = 0; void send(text); }
      else handleMiss("Konuşma anlaşılamadı");
    } catch (e) { setVoiceErr((e as Error).message); resumeIfConvo(); }
  };
  const startListening = async () => {
    if (!convoRef.current) return;
    // ÇİFT KAYIT KİLİDİ: hâlâ kayıt sürerken ikinci kayıt başlatılırsa aynı akış üzerinde
    // iki MediaRecorder çakışıyor ve mikrofon sessize düşüyordu ("asistan durdu" hissi).
    if (recorderRef.current?.state === "recording") return;
    stopStandby(); // bekleme mikrofonu ile sohbet kaydı AYNI ANDA çalışmasın
    try {
      if (!streamRef.current) streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const stream = streamRef.current;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        clearVad();
        setListening(false);
        const blob = new Blob(chunksRef.current, { type: mime });
        if (!convoRef.current) return;
        if (spokeRef.current && blob.size > 2000) void transcribeAndSend(blob);
        else if (silentTimeoutRef.current) {
          // Pencere boyunca HİÇ konuşma algılanmadı → sessizce döngüye girmek yerine haber ver
          silentTimeoutRef.current = false;
          handleMiss("Mikrofondan ses gelmedi");
        } else void startListening();
      };
      recorderRef.current = recorder;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      spokeRef.current = false; lastLoudAtRef.current = 0; startedAtRef.current = Date.now(); silentTimeoutRef.current = false;
      recorder.start(250);
      setListening(true); setOrb("listening");
      clearVad();
      vadTimerRef.current = window.setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        // Eşik 0.025 → 0.016: kısık/uzak mikrofonda konuşma algılanmıyordu
        if (rms > 0.016) { spokeRef.current = true; lastLoudAtRef.current = now; }
        const silentTooLong = !spokeRef.current && now - startedAtRef.current > 25_000;
        if (silentTooLong) silentTimeoutRef.current = true; // onstop bunu görüp sesli uyarı verir
        // 1700 → 1150 ms: canlı geri bildirim "cevap çok geç geliyor"du; yarım
        // saniye buradan geliyor. 1150, doğal cümle içi duraksamayı hâlâ bekler.
        if ((spokeRef.current && lastLoudAtRef.current && now - lastLoudAtRef.current > 1150) || silentTooLong || now - startedAtRef.current > 60_000) stopListening();
      }, 150);
    } catch (e) {
      convoRef.current = false;
      setListening(false); setOrb("idle");
      setVoiceErr((e as Error)?.name === "NotAllowedError" ? "Mikrofon izni verilmedi." : "Mikrofon açılamadı.");
    }
  };
  const toggleMic = () => {
    if (convoRef.current) { endConversation(); return; }
    convoRef.current = true;
    assistantStore.setVoice(true);
    void startListening();
  };
  useEffect(() => () => { stopVoice(); endConversation(true); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // AKTİF KONUŞMACI: bu bileşen monteyken tur/anlatım sesi BURADAN çıkar. Unmount olunca
  // kaydı bırakır → kapanmış bileşen arkadan konuşmaya devam etmez (ses üst üste binmesi).
  // YALNIZ SES AÇIKKEN kaydol (tur bulgusu): ses kapalı bileşenin speak()'i anında
  // döndüğü için turun okuma beklemesini iptal ediyor, 19 durak koşarak geçiyordu.
  // Kayıt yoksa tur, başlangıçta verilen konuşmacıya ya da okuma süresine düşer.
  useEffect(() => {
    assistantStore.setSpeaker(voiceOn ? (t: string) => speak(t) : null);
    return () => { if (assistantStore.hasSpeaker()) assistantStore.setSpeaker(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceOn]);

  // SESLİ MOD DIŞARIDAN (sidebar orb'u): store.voice değişimini eller-serbest döngüye bağla
  const { voice: storeVoice, pendingPrompt } = useAssistantStore();

  // BAŞKA SAYFADAN GELEN HAZIR İSTEM (ör. Planlama → "Strateji Seansı"): panel açılınca gönder
  useEffect(() => {
    if (!pendingPrompt || busy || !activeDomainId) return;
    const p = assistantStore.consumePendingPrompt();
    if (p) void send(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, busy, activeDomainId]);
  useEffect(() => {
    if (!voiceControlled) return;
    if (storeVoice && !convoRef.current) { convoRef.current = true; void startListening(); }
    else if (!storeVoice && convoRef.current) endConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeVoice, voiceControlled]);

  // Düşünürken dönen durum metni ("analiz ediyorum…")
  const [busyIdx, setBusyIdx] = useState(0);
  useEffect(() => {
    if (!busy) return;
    setBusyIdx(0);
    const t = window.setInterval(() => setBusyIdx((i) => i + 1), 2200);
    return () => window.clearInterval(t);
  }, [busy]);

  const clearHistory = async () => {
    if (!activeDomainId) return;
    // Tarayıcı popup'ı değil, site şablonlu onay kutusu
    if (!(await confirmDialog({
      title: convId ? "Bu sohbet silinsin mi?" : "Tüm sohbet geçmişi silinsin mi?",
      description: convId ? "Yalnız açık olan sohbet silinir." : "Bu domaindeki bütün sohbetler kalıcı olarak silinir.",
      confirmText: "Sil", danger: true,
    }))) return;
    await fetch(`/api/domains/${activeDomainId}/assistant/history${convId ? `?conversationId=${convId}` : ""}`, { method: "DELETE" });
    setMessages([]);
    setConvId(0);
    queryClient.invalidateQueries({ queryKey: ["assistant-history", activeDomainId] });
    queryClient.invalidateQueries({ queryKey: ["assistant-conversations", activeDomainId] });
  };

  // ÇEKİDÜZEN: hazır soru çipleri SAYFAYA GÖRE zenginleşir — "ne soracağımı bilmiyorum"
  // duvarını yıkar. İlk eşleşen kural kazanır; genel üçlü her zaman sonda durur.
  const SAYFA_SORULARI: { eslesir: (p: string) => boolean; sorular: string[] }[] = [
    { eslesir: (p) => p === "/" || p.startsWith("/kokpit"), sorular: ["Sitemin genel sağlığı nasıl, kısaca özetle", "Bu hafta hangi 3 işe odaklanmalıyım?"] },
    { eslesir: (p) => p.startsWith("/firsatlar"), sorular: ["Bu fırsatlardan hangisi en hızlı sonuç verir?"] },
    { eslesir: (p) => p.startsWith("/nobetci"), sorular: ["Son olaylardan endişelenmem gereken var mı?"] },
    { eslesir: (p) => p.startsWith("/tech-audit"), sorular: ["En kritik teknik hatayı bana basitçe anlat", "Bu hataları web sitecime nasıl anlatırım?"] },
    { eslesir: (p) => p.startsWith("/rank-tracking"), sorular: ["Sıralamalarım geçen haftaya göre nasıl?"] },
    { eslesir: (p) => p.startsWith("/ai-firsatlar") || p.startsWith("/backlink-firsat"), sorular: ["AI cevaplarında nasıl daha çok anılırım?"] },
    { eslesir: (p) => p.startsWith("/tazelik"), sorular: ["Önce hangi içeriği tazelemeliyim?"] },
    { eslesir: (p) => p.startsWith("/keyword-research") || p.startsWith("/content-planner"), sorular: ["Hangi kelimeyle içerik üretmeye başlamalıyım?"] },
    { eslesir: (p) => p.startsWith("/sxo"), sorular: ["Dönüşüm kaybettiren sayfalarımı nasıl düzeltirim?"] },
  ];
  const sayfaSorulari = SAYFA_SORULARI.find((k) => k.eslesir(location))?.sorular ?? [];
  const SUGGESTIONS = [...sayfaSorulari, "Genel durum nedir, önce ne yapmalıyım?", "Bu sayfada bana yardım et", "En kritik hatalarım neler?"];

  const chatColumn = (
    <div className="flex flex-col h-full min-h-0 flex-1 rounded-2xl border bg-white overflow-hidden dark:bg-gray-900" style={{ borderColor: "#e7e7e4" }}>
      {/* Başlık şeridi: mini orb + durum + kontroller */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0" style={{ borderColor: "#e7e7e4" }}>
        <AssistantOrb state={orb} size={compact ? 44 : 56} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">Geolop Asistanı</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {orb === "listening" ? "seni dinliyorum, sustuğunda gönderirim" : orb === "speaking" ? "konuşuyor" : orb === "thinking" || busy ? "yazıyor…" : "hazır"}
          </div>
        </div>
        <button onClick={newConvo} title="Yeni sohbet" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><Plus className="w-4 h-4" /></button>
        <button onClick={() => setWakeOn((v) => !v)} title={wakeOn ? "Uyandırma modu AÇIK (çift alkış ya da 'uyan' de)" : "Uyandırma modunu aç: çift alkış ya da 'uyan' de, sohbet başlasın"}
          className="p-1.5 rounded-lg hover:bg-muted"
          style={wakeOn ? { color: "#e83e3c", background: "#fdecea" } : { color: "#a3a3a0" }}>
          <Ear className="w-4 h-4" />
        </button>
        {/* Ses düğmesi: kapatınca O ANDA çalan ses de durur (yalnız gelecek cevaplar değil) +
            kapalı durum kırmızı gösterilir ki "çalışmıyor" sanılmasın */}
        <button onClick={() => setVoiceOn((v) => { const next = !v; if (!next) stopVoice(); return next; })}
          title={voiceOn ? "Sesi kapat (çalan ses de durur)" : "Ses KAPALI — açmak için tıkla"}
          className="p-1.5 rounded-lg hover:bg-muted"
          style={voiceOn ? { color: "var(--muted-foreground)" } : { color: "#e83e3c", background: "#fdecea" }}>
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <button onClick={clearHistory} title="Geçmişi temizle" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><Trash2 className="w-4 h-4" /></button>
        {/* Kapatma çarpısı başlık SATIRINDA yaşar — mutlak konumlu çarpı çöp ikonuna biniyordu */}
        {onClose && <button onClick={onClose} title="Paneli kapat" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X className="w-4 h-4" /></button>}
      </div>
      {voiceErr && <div className="text-[10px] px-3 py-1" style={{ color: "#b12220" }}>🔇 {voiceErr}</div>}
      {wakeOn && !convoRef.current && (
        <div className="px-3 py-1 text-[10px] text-muted-foreground">
          👂 Uyandırma açık — çift alkış ya da “uyan” de.{wakeHeard ? ` Son duyulan: “${wakeHeard}”` : ""}
        </div>
      )}

      {/* Mesajlar */}
      <div className="flex-1 overflow-y-auto space-y-3 p-3 min-h-0">
        {messages.length === 0 && (
          <div className="text-center pt-4">
            <div className="text-xs text-muted-foreground mb-3">Buradayım. Sitenin tüm verilerini biliyorum, hangi sayfadaysan ona göre yardım ederim.</div>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => void send(s)} className="text-[11px] px-2.5 py-1 rounded-full border hover:shadow-sm transition text-[#b12220] dark:text-red-300 dark:border-red-400/40" style={{ borderColor: "#f7b6b4" }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id ?? `t${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%]">
              <div className={`rounded-2xl px-3 py-2 text-[13px] whitespace-pre-wrap ${m.role === "user" ? "text-white" : "border bg-white border-[#e7e7e4] text-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90"}`}
                style={m.role === "user" ? { background: "#e83e3c" } : undefined}>
                {m.content}
              </div>
              {(m.meta as { chart?: { title: string; type: "line" | "bar"; xKey: string; seriesKeys: string[]; rows: Record<string, string | number>[] } })?.chart && (() => {
                const ch = (m.meta as { chart: { title: string; type: "line" | "bar"; xKey: string; seriesKeys: string[]; rows: Record<string, string | number>[] } }).chart;
                const TONES = ["#e83e3c", "#ec6461", "#b12220", "#f18e8b"];
                return (
                  <Card className="mt-1.5"><CardContent className="p-2.5">
                    <div className="text-[11px] font-semibold mb-1" style={{ color: "#b12220" }}>{ch.title}</div>
                    <ResponsiveContainer width="100%" height={180}>
                      {ch.type === "bar" ? (
                        <BarChart data={ch.rows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                          <XAxis dataKey={ch.xKey} tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} />
                          <RTooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
                          {ch.seriesKeys.map((k, i) => <Bar key={k} dataKey={k} fill={TONES[i % 4]} radius={[3, 3, 0, 0]} />)}
                        </BarChart>
                      ) : (
                        <LineChart data={ch.rows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                          <XAxis dataKey={ch.xKey} tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} />
                          <RTooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
                          {ch.seriesKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={TONES[i % 4]} strokeWidth={2} dot={false} />)}
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </CardContent></Card>
                );
              })()}
              {m.meta?.table && (
                <Card className="mt-1.5"><CardContent className="p-2.5">
                  <div className="text-[11px] font-semibold mb-1" style={{ color: "#b12220" }}>{m.meta.table.title}</div>
                  <div className="overflow-x-auto">
                    <table className="text-[10px] w-full">
                      <thead><tr className="border-b text-muted-foreground">{m.meta.table.columns.map((c) => <th key={c} className="text-left font-medium py-0.5 pr-2">{c}</th>)}</tr></thead>
                      <tbody>{m.meta.table.rows.map((r, ri) => <tr key={ri} className="border-b last:border-0">{r.map((c, ci) => <td key={ci} className="py-0.5 pr-2">{c}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                </CardContent></Card>
              )}
              {(m.meta?.actions?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {m.meta!.actions!.map((a, ai) => (
                    <Button key={ai} size="sm" variant="outline" className="text-[11px] h-7 gap-1" style={{ borderColor: "#f79009", color: "#b12220" }} onClick={() => setLocation(a.path)}>
                      {a.label} <ArrowRight className="w-3 h-3" />
                    </Button>
                  ))}
                </div>
              )}
              {/* ONAY KARTI (v2-E): ücretli işlem yalnız buradan başlar */}
              {m.meta?.approval && !approvalDone.has(i) && (
                <div className="mt-1.5 rounded-xl border p-2.5" style={{ borderColor: "rgba(245,158,11,0.5)", background: "#fffbeb" }}>
                  <div className="text-[12px] font-semibold" style={{ color: "#b54708" }}>{m.meta.approval.label} başlatılsın mı?</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Tahmini maliyet ~{m.meta.approval.tokens.toLocaleString("tr-TR")} token</div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Button size="sm" className="h-7 text-[11px] text-white" style={{ background: "linear-gradient(135deg,#b54708,#f79009)" }}
                      disabled={approvalBusy === i}
                      onClick={() => void approveTool(i, m.meta!.approval!)}>
                      {approvalBusy === i ? "Başlatılıyor…" : "Onayla ve Başlat"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground"
                      onClick={() => setApprovalDone((s) => new Set(s).add(i))}>Vazgeç</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="rounded-2xl px-3 py-2 border flex items-center gap-2" style={{ borderColor: "#e7e7e4" }}>
            <span className="inline-flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#e83e3c", animationDelay: `${i * 0.15}s` }} />)}</span>
            <span className="text-[11px] text-muted-foreground">{BUSY_TEXTS[busyIdx % BUSY_TEXTS.length]}</span>
          </div></div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Giriş — SABİT: mesajlar kayar, bu çubuk hep altta kalır */}
      <div className="shrink-0 p-2 sticky bottom-0 border-t bg-gray-50 dark:bg-gray-900" style={{ borderColor: "#e7e7e4" }}>
        {/* Eklenen dosya: gönderilmeden önce görünür ve kaldırılabilir olmalı */}
        {(ek || ekIkili) && (
          <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded-lg border text-[11px]" style={{ borderColor: "#f7b6b4", background: "#fff7f7" }}>
            <Paperclip className="w-3 h-3 shrink-0" style={{ color: "#b12220" }} />
            <span className="truncate flex-1">{ek?.ad ?? ekIkili?.ad}{ekIkili && <span className="ml-1 text-muted-foreground">({ekIkili.tur === "gorsel" ? "görsel" : ekIkili.tur === "pdf" ? "PDF" : "Excel"})</span>}</span>
            <span className="text-muted-foreground shrink-0">{Math.round((ek ? ek.metin.length : ekIkili!.boyut) / 1024)} KB</span>
            <button onClick={() => { setEk(null); setEkIkili(null); if (dosyaRef.current) dosyaRef.current.value = ""; }} title="Kaldır" className="shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        {/* Model seçimi: hız/derinlik dengesini kullanıcı kurar.
            Sesli görüşme her zaman Hızlı ile koşar (ölçüldü: ilk cümle 5.8sn vs 15.8sn). */}
        <div className="flex items-center gap-1 mb-1.5">
          {(["hizli", "denge", "derin"] as const).map((m) => (
            <button key={m} onClick={() => modelSec(m)} title={MODEL_NOT[m]}
              className={`px-2 py-0.5 rounded-full text-[10px] border transition ${model === m ? "font-semibold" : "opacity-60 hover:opacity-100"}`}
              style={model === m ? { background: "#fdecea", borderColor: "#f7b6b4", color: "#b12220" } : { borderColor: "#e7e7e4" }}>
              {MODEL_AD[m]}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 items-center rounded-xl border px-2 py-1.5 bg-white dark:bg-gray-800 dark:border-red-400/40" style={{ borderColor: "#f7b6b4" }}>
          <button onClick={toggleMic} title={convoRef.current ? "Sesli sohbeti bitir" : "Sesli sohbeti başlat"}
            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition ${listening ? "animate-pulse" : ""}`}
            style={listening ? { background: "#e83e3c", color: "#fff" } : { background: "#fdecea", color: "#b12220" }}>
            <Mic className="w-3.5 h-3.5" />
          </button>
          <input ref={dosyaRef} type="file" accept={DOSYA_TURLERI} className="hidden"
            onChange={(e) => void dosyaSec(e.target.files?.[0])} />
          <button onClick={() => dosyaRef.current?.click()} title="Dosya ekle (metin, görsel, PDF, Excel)"
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition hover:bg-gray-100 dark:hover:bg-white/10"
            style={{ color: "#b12220" }}>
            <Paperclip className="w-3.5 h-3.5" />
          </button>
          {/* disabled={busy} KALDIRILDI (kullanıcı: "asistan konuşurken yazamıyoruz") —
              cevap akarken de yazılabilir; Enter cevap bitince gönderilebilir, buton Durdur'dadır */}
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !busy) void send(); }}
            placeholder="Sor ya da mikrofona bas…" className="flex-1 text-[13px] outline-none bg-transparent text-gray-800 placeholder:text-gray-400 dark:text-white/90 dark:placeholder:text-gray-500" />
          {/* DURDUR: cevap akarken gönder butonu durdurmaya döner. Yazılan kısım
              ekranda ve veritabanında kalır — durdurmak cevabı silmez. */}
          {busy ? (
            <Button size="sm" onClick={durdur} title="Cevabı durdur"
              className="gap-1 shrink-0 h-8 px-2.5" style={{ background: "#6b7280" }}>
              <Square className="w-3 h-3 fill-current" />
            </Button>
          ) : (
            <Button size="sm" onClick={() => void send()} disabled={!input.trim()} className="gap-1 shrink-0 h-8 px-2.5" style={{ background: "#e83e3c" }}>
              <Send className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  if (!withThreads) return chatColumn;

  // Tam sayfa: SOLDA sohbet geçmişi (ChatGPT düzeni), sağda sohbet
  return (
    <div className="flex h-full min-h-0 gap-3">
      <div className="w-64 shrink-0 hidden lg:flex flex-col rounded-2xl border overflow-hidden bg-white border-[#e7e7e4] dark:bg-gray-900 dark:border-gray-800">
        <div className="p-2.5 border-b shrink-0" style={{ borderColor: "#e7e7e4" }}>
          <Button size="sm" onClick={newConvo} className="w-full gap-1.5 h-8 text-[12px]" style={{ background: "#e83e3c" }}>
            <Plus className="w-3.5 h-3.5" /> Yeni Sohbet
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
          {(convos ?? []).length === 0 && <div className="text-[11px] text-muted-foreground text-center pt-6">Henüz sohbet yok</div>}
          {(convos ?? []).map((c) => (
            // Satır div: button içine button koymak geçersiz HTML — tıklama alanı
            // ve silme ikonu ayrı butonlar. Silme ikonu hover'da belirir (ChatGPT
            // deseni); dokunmatik cihazlarda hover olmadığı için hep görünür kalır.
            <div key={c.id} className="group relative">
              <button onClick={() => openConvo(c.id)}
                className={`w-full text-left rounded-xl px-2.5 py-2 pr-8 transition border ${c.id === convId ? "bg-[#fdecea] dark:bg-red-500/15" : "bg-transparent"}`}
                style={c.id === convId ? { borderColor: "#f7b6b4" } : { borderColor: "transparent" }}
                data-aktif={c.id === convId ? "1" : undefined}>
                <div className="flex items-start gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#b12220" }} />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium truncate text-gray-800 dark:text-white/90">{c.title}</div>
                    <div className="text-[10px] text-muted-foreground">{new Date(c.lastAt).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} · {c.count} mesaj</div>
                  </div>
                </div>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); void sohbetiSil(c.id, c.title); }}
                title="Sohbeti sil"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-500 lg:opacity-0 lg:group-hover:opacity-100 focus:opacity-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
      {chatColumn}
    </div>
  );
}
